import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { catchError, defer, of, repeat, Subject, Subscription, switchMap, takeUntil, tap, timer } from 'rxjs';

export interface CoinwiseUser {
  id?: number;
  username: string;
  type?: string;
  is_youtube?: boolean;
  is_twitch?: boolean;
  amount: number;
  keyword?: string;
}

export interface CoinwiseEntriesResponse {
  state: string;
  start_time: number | null;
  keyword: string | null;
  entries: CoinwiseUser[] | null;
  subscription_count: number | null;
  subscription_title: string | null;
}

/** `?testMode=` options — simulate a full giveaway flow without the backend. */
export interface GiveawayTestOptions {
  max: number;        // total participants to fill to
  rate: number;       // participants joining per second
  keyword: string;    // entry keyword shown in the header
  countdown: number;  // seconds of countdown before entries open
  winners: number;    // how many winners to draw
  hold: number;       // seconds to keep the full list before drawing winners
  loop: boolean;      // repeat the whole cycle
}

@Injectable({
  providedIn: 'root',
})
export class CoinwiseEntriesService {
  private readonly endpoint = `${this.getBaseUrl()}/api/overlay/entries`;
  private usersSignal = signal<CoinwiseUser[]>([]);
  private giveawayDataSignal = signal<CoinwiseEntriesResponse | null>(null);
  private pollingSubscription?: Subscription;
  private stopPolling$ = new Subject<void>();
  private testTimers: ReturnType<typeof setInterval>[] = [];
  private testCounter = 0;

  constructor(private http: HttpClient) {}

  private getBaseUrl(): string {
    return window.location.hostname.includes('kimba.tv')
      ? 'https://coinwise.com'
      : 'https://staging.coinwise.com';
  }

  public startUsersPolling(): void {
    if (this.pollingSubscription) return;

    // Self-scheduling poll: request → apply → wait → repeat. The wait adapts to
    // the current state (POLL_ACTIVE_MS while a giveaway is live, POLL_IDLE_MS
    // while idle), so it's instant during a giveaway but cheap 24/7. Spacing the
    // next request AFTER each response also means requests never pile up.
    const pollOnce$ = defer(() =>
      this.http.get<CoinwiseEntriesResponse>(this.endpoint).pipe(
        catchError((err) => {
          console.error('Coinwise API request failed, polling continues:', err);
          return of(null);
        }),
      ),
    ).pipe(
      tap((response) => {
        if (response) this.applyResponse(response);
      }),
      switchMap(() => timer(this.pollDelayMs())),
    );

    this.pollingSubscription = pollOnce$
      .pipe(repeat(), takeUntil(this.stopPolling$))
      .subscribe({
        error: (err) => console.error('Coinwise polling stream terminated:', err),
      });
  }

  private applyResponse(response: CoinwiseEntriesResponse): void {
    this.giveawayDataSignal.set(response);
    if (response.state === 'idle' || !response.entries) {
      this.clearUsers();
    } else {
      this.processUsers(response.entries, response.keyword);
    }
  }

  /** 1s while a giveaway is live, 5s while idle. */
  private pollDelayMs(): number {
    const state = this.giveawayDataSignal()?.state;
    return state && state !== 'idle' ? 1000 : 5000;
  }

  public stopUsersPolling(): void {
    this.stopPolling$.next();
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  // ── Test mode (?testMode=1&max=&rate=&keyword=&countdown=&winners=&hold=&loop=) ──
  // Drives the full giveaway flow (countdown -> entries fill -> winners -> idle)
  // entirely client-side, so the overlay can be tested at any participant count
  // in OBS/browser without the backend. Does NOT poll the API.

  public startTestMode(opts: GiveawayTestOptions): void {
    this.stopTestMode();
    this.testCounter = 0;
    this.runTestCountdown(opts);
  }

  public stopTestMode(): void {
    for (const t of this.testTimers) {
      clearTimeout(t);
      clearInterval(t);
    }
    this.testTimers = [];
  }

  private testResponse(partial: Partial<CoinwiseEntriesResponse>): CoinwiseEntriesResponse {
    return {
      state: 'idle', start_time: null, keyword: null, entries: null,
      subscription_count: null, subscription_title: null, ...partial,
    };
  }

  private runTestCountdown(opts: GiveawayTestOptions): void {
    this.usersSignal.set([]);
    if (opts.countdown > 0) {
      this.giveawayDataSignal.set(this.testResponse({
        state: 'countdown',
        start_time: Math.floor((Date.now() + opts.countdown * 1000) / 1000),
        keyword: opts.keyword,
        subscription_count: opts.winners,
        subscription_title: 'Premium',
      }));
      this.testTimers.push(setTimeout(() => this.runTestEntries(opts), opts.countdown * 1000));
    } else {
      this.runTestEntries(opts);
    }
  }

  private runTestEntries(opts: GiveawayTestOptions): void {
    this.giveawayDataSignal.set(this.testResponse({
      state: 'active',
      keyword: opts.keyword,
      subscription_count: opts.winners,
      subscription_title: 'Premium',
    }));
    this.usersSignal.set([]);
    this.testCounter = 0;

    const join = setInterval(() => {
      if (this.testCounter >= opts.max) {
        clearInterval(join);
        this.testTimers.push(setTimeout(() => this.runTestWinners(opts), opts.hold * 1000));
        return;
      }
      this.testCounter++;
      this.usersSignal.update((u) => [...u, this.makeTestEntry(this.testCounter)]);
    }, Math.max(30, Math.round(1000 / Math.max(0.1, opts.rate))));
    this.testTimers.push(join);
  }

  private runTestWinners(opts: GiveawayTestOptions): void {
    const all = this.usersSignal();
    const winners = all.slice(0, Math.max(1, Math.min(opts.winners, all.length)));
    this.giveawayDataSignal.set(this.testResponse({
      state: 'winners',
      keyword: opts.keyword,
      subscription_count: opts.winners,
      subscription_title: 'Premium',
    }));
    this.usersSignal.set(winners);

    this.testTimers.push(setTimeout(() => {
      this.giveawayDataSignal.set(this.testResponse({ state: 'idle' }));
      this.usersSignal.set([]);
      if (opts.loop) {
        this.testTimers.push(setTimeout(() => this.runTestCountdown(opts), 3000));
      }
    }, 8000));
  }

  private makeTestEntry(n: number): CoinwiseUser {
    const both = n % 7 === 0;
    return {
      id: n,
      username: `viewer_${n}`,
      is_twitch: both || n % 2 === 0,
      is_youtube: both || n % 2 === 1,
      amount: 1,
    };
  }

  private processUsers(users: CoinwiseUser[], keyword?: string | null): void {
    const mappedUsers = users.map((user: CoinwiseUser) => ({
      ...user,
      keyword: keyword || user.keyword,
    }));

    if (this.giveawayDataSignal()?.state === 'winners') {
      this.usersSignal.set([...mappedUsers]);
    } else {
      this.usersSignal.update((prev) => {
        const newUsersList = [...prev];

        for (const user of mappedUsers) {
          const existingUserIndex = newUsersList.findIndex((u) => u.username === user.username);

          if (existingUserIndex >= 0) {
            newUsersList[existingUserIndex] = {
              ...newUsersList[existingUserIndex],
              ...user,
              id: newUsersList[existingUserIndex].id,
            };
          } else {
            newUsersList.push(user);
          }
        }

        return newUsersList;
      });
    }
  }

  private clearUsers(): void {
    this.usersSignal.set([]);
  }

  public getUsers() {
    return this.usersSignal.asReadonly();
  }

  public getGiveawayData() {
    return this.giveawayDataSignal.asReadonly();
  }
}
